import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption, H6 } from '@/design-system/components/typography';
import { ScrollView, SxProp, View } from 'dripsy';
import _, { isString } from 'lodash';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { CreateCardAccountStackParamList, CreateCardAccountStackRoutes } from './type';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { CardAccountTypeSelectFilters, CardCurrencySelectFilters, CardIdentityTypeSelectFilters } from '../../options';
import { errorParser } from '@/features/bill-payment/screens/otp/type';
import { formatAmountByDecimal } from '@/features/account-management/utils';

export interface CreateCardAccountProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  data: any;
  func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  onChangeView?: () => void;
}

export const CreateCardAccountWebOneScreen: React.FC<CreateCardAccountProps> = (props) => {
  const { onClose, onLayout, data, func, onReturnData, refresh, onChangeView } = props;
  const { t } = useTranslation('card-account');
  const layout = useContext(LayoutContext);
  const { body, textMb, textTitle, textAtmPin, btnView, btn } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<CreateCardAccountStackParamList>>();

  const [alertMessage, setAlertMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const submitData = {
    name: data.name,
    phoneNumber: `${data.phoneCode}${data.phoneNumber}`,
    identityType: data.identityType,
    accountType: data.accountType,
    currency: data.currency,
    assetLink: data.accountType == 'asset-link' && data.assetLink ? { accountId: data.assetLink.id } : undefined,
    individual: data.individual && data?.identityType == 'individual' ? data?.individual : undefined,
    creditAccount: data.accountType == 'fixed-limit' && data.creditAccount ? [data.creditAccount.id] : undefined,
    limit: data.accountType == 'fixed-limit' && data.limit ? data.limit : undefined,
    settlementAccount: data.accountType == 'fixed-limit' && data.settlementAccount ? data.settlementAccount.id : undefined
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader
        title={t('card-account::credit-card-new-account.new-account-confirmation.title--new-account-instruction-confirmation')}
        closeModal={() => navigation.getParent()?.goBack()}
      />
      <ScrollView sx={{ flex: 1, backgroundColor: 'transparent' }} nestedScrollEnabled>
        <View sx={body}>
          <TextBody sx={textMb} semiBold>
            {t('card-account::credit-card-new-account.new-account-confirmation.title--create-new-account-inquiry')}
          </TextBody>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('card-account::credit-card-new-account.glossary.account-name')}
            </TextCaption>
            <H6 sx={textAtmPin}>{data.name}</H6>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('card-account::credit-card-new-account.glossary.mobile-number')}
            </TextCaption>
            <H6 sx={textAtmPin}>{`+${data.phoneCode} ${data.phoneNumber}`}</H6>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('card-account::credit-card-new-account.glossary.account-type')}
            </TextCaption>
            <H6 sx={textAtmPin}>{CardIdentityTypeSelectFilters(t).find((el) => el.value === data.identityType)?.label}</H6>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('card-account::credit-card-new-account.glossary.card-type')}
            </TextCaption>
            <H6 sx={textAtmPin}>{CardAccountTypeSelectFilters(t).find((el) => el.value === data.accountType)?.label}</H6>
          </View>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('card-account::credit-card-new-account.glossary.currency')}
            </TextCaption>
            <H6 sx={textAtmPin}>{CardCurrencySelectFilters(t).find((el) => el.value === data.currency)?.label}</H6>
          </View>
          {data.accountType == 'asset-link' && data.assetLink && (
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account::credit-card-new-account.glossary.account')}
              </TextCaption>
              <H6 sx={textAtmPin}>
                {data.assetLink.name} ({data.assetLink.accountNumber})
              </H6>
            </View>
          )}
          {data.accountType == 'fixed-limit' && data.creditAccount && (
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account::credit-card-new-account.glossary.credit-account')}
              </TextCaption>
              <H6 sx={textAtmPin}>
                {data.creditAccount.name} ({data.creditAccount.accountNumber})
              </H6>
            </View>
          )}
          {data.accountType == 'fixed-limit' && data.limit && (
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account::credit-card-new-account.glossary.limit')}
              </TextCaption>
              <H6 sx={textAtmPin}>
                {formatAmountByDecimal({ value: data.limit, decimals: 2 })} {data.currency}
              </H6>
            </View>
          )}
          {data.accountType == 'fixed-limit' && data.settlementAccount && (
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account::credit-card-new-account.glossary.settlement-account')}
              </TextCaption>
              <H6 sx={textAtmPin}>
                {data.settlementAccount.name} ({data.settlementAccount.accountNumber})
              </H6>
            </View>
          )}
          {data.individual && data?.identityType == 'individual' && (
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account::credit-card-new-account.glossary.account-holder')}
              </TextCaption>
              <H6 sx={textAtmPin}>{`${data.individual?.firstName} ${data.individual?.lastName}`}</H6>
            </View>
          )}
          {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
          <View sx={btnView}>
            <UniButton
              sx={btn}
              variant={ButtonVariant.outline1}
              label={t('card-account-management::set-card-limit.button--back')}
              onPress={() => {
                navigation?.goBack();
                onChangeView && onChangeView();
              }}
            />
            <UniButton
              sx={btn}
              labelSx={{ color: '$balck' }}
              label={t('card-account-management::general.button--place-instruction')}
              loading={loading}
              disabled={loading}
              onPress={() => {
                setLoading(true);
                func &&
                  func(
                    submitData,
                    (returnData) => {
                      setLoading(false);
                      onReturnData && onReturnData(returnData.data);
                      navigation.navigate(CreateCardAccountStackRoutes.Placed, {
                        onLayout: onLayout,
                        data,
                        returnData: returnData.data,
                        refresh
                      });
                    },
                    (error) => {
                      setLoading(false);
                      const data: any = error.response?.data;
                      const errorMessage = data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
                      setAlertMessage(errorMessage);
                    }
                  );
              }}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28,
    gap: '$5'
  },
  textMb: {
    marginBottom: 20
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    marginTop: 40
  },
  btn: {
    width: 216,
    height: 44
  }
};
